import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, Clock, CalendarDays, MapPin, ArrowRight, Stethoscope, User,
  List, ChevronLeft, ChevronRight, X, Eye, CheckCircle2,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';

/* ═══ Config ═══ */
const priorityConfig = {
  alta: { label: 'Alta', dot: 'bg-destructive', badge: 'alta' },
  media: { label: 'Media', dot: 'bg-warning', badge: 'media' },
  bassa: { label: 'Bassa', dot: 'bg-success', badge: 'bassa' },
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const VISIT_TIMES = ['08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '14:00', '14:30', '15:00', '15:30', '16:00'];

/* ═══ Helpers ═══ */
function parseDate(str) {
  if (!str) return null;
  const p = str.split('/');
  return p.length === 3 ? new Date(+p[2], +p[1] - 1, +p[0]) : null;
}

function assignTime(idx) {
  return VISIT_TIMES[idx % VISIT_TIMES.length];
}

function isToday(d) {
  const t = new Date();
  return d && d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

function formatRelativeDate(d) {
  if (!d) return '';
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - now) / 86400000);
  if (diff === 0) return 'Oggi';
  if (diff === 1) return 'Domani';
  if (diff === -1) return 'Ieri';
  if (diff > 0 && diff <= 6) return `Tra ${diff} giorni`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

/* ═══ Calendar Grid (Touch-Optimized) ═══ */
function MonthCalendar({ events, month, year, onNav, onEventClick }) {
  const first = new Date(year, month, 1);
  let offset = first.getDay() - 1;
  if (offset < 0) offset = 6;
  const days = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const weeks = [];
  let cur = 1 - offset;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) { week.push(cur); cur++; }
    weeks.push(week);
    if (cur > days) break;
  }

  const eventsFor = (day) => day >= 1 && day <= days
    ? events.filter(e => e.date.getFullYear() === year && e.date.getMonth() === month && e.date.getDate() === day).sort((a, b) => a.ora.localeCompare(b.ora))
    : [];

  const isTod = (day) => day >= 1 && day <= days && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="icon" onClick={() => onNav(-1)} className="h-10 w-10" data-testid="cal-prev-month">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h3 className="text-lg font-bold text-foreground" data-testid="cal-month-label">
          {MONTHS_IT[month]} {year}
        </h3>
        <Button variant="ghost" size="icon" onClick={() => onNav(1)} className="h-10 w-10" data-testid="cal-next-month">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-px mb-1">
        {WEEKDAYS.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-border rounded-xl overflow-hidden">
        {weeks.flat().map((day, i) => {
          const inMonth = day >= 1 && day <= days;
          const dayEv = eventsFor(day);
          return (
            <div key={i} className={`min-h-[100px] p-1.5 ${inMonth ? 'bg-card' : 'bg-muted/30'}`}>
              {inMonth && (
                <>
                  <div className={`text-xs font-semibold mb-1 w-7 h-7 flex items-center justify-center rounded-full ${isTod(day) ? 'bg-primary text-primary-foreground' : 'text-foreground'}`}>
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {dayEv.slice(0, 3).map(ev => (
                      <button
                        key={ev.id}
                        onClick={() => onEventClick(ev.patientId)}
                        className="w-full text-left text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/15 text-primary hover:bg-primary/25 truncate transition-colors"
                        data-testid={`cal-event-${ev.id}`}
                      >
                        {ev.ora} {ev.cognome}
                      </button>
                    ))}
                    {dayEv.length > 3 && <span className="text-[9px] text-muted-foreground pl-1">+{dayEv.length - 3}</span>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ Main Component ═══ */
export default function MedicoInbox() {
  const { patients, showPendingNotifications } = useApp();
  const navigate = useNavigate();

  useEffect(() => { showPendingNotifications('medico'); }, [showPendingNotifications]);

  const [tab, setTab] = useState('da_iniziare');
  const [viewMode, setViewMode] = useState('lista');
  const [search, setSearch] = useState('');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  /* Patients assigned to medico */
  const assigned = useMemo(() => {
    return patients
      .filter(p => ['presa_in_carico', 'in_valutazione', 'pai_completato'].includes(p.stato))
      .map((p, i) => ({
        ...p,
        oraVisita: assignTime(i),
        visitDate: parseDate(p.dataVisitaUVMD),
      }));
  }, [patients]);

  const daIniziare = useMemo(() => assigned.filter(p => p.stato !== 'pai_completato'), [assigned]);
  const completate = useMemo(() => assigned.filter(p => p.stato === 'pai_completato'), [assigned]);

  const filtered = useMemo(() => {
    const list = tab === 'da_iniziare' ? daIniziare : completate;
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(p => `${p.cognome} ${p.nome} ${p.codiceFiscale}`.toLowerCase().includes(q));
  }, [tab, daIniziare, completate, search]);

  const calEvents = useMemo(() => {
    return assigned.filter(p => p.visitDate).map(p => ({
      id: `ev-${p.id}`,
      patientId: p.id,
      cognome: p.cognome,
      nome: p.nome,
      ora: p.oraVisita,
      date: p.visitDate,
      priorita: p.priorita,
    }));
  }, [assigned]);

  const handleCalNav = useCallback((dir) => {
    setCalMonth(prev => {
      let m = prev + dir;
      if (m > 11) { setCalYear(y => y + 1); return 0; }
      if (m < 0) { setCalYear(y => y - 1); return 11; }
      return m;
    });
  }, []);

  return (
    <AppLayout role="medico">
      <div className="space-y-5">
        {/* ── Header ── */}
        <div className="flex flex-col gap-4" data-testid="inbox-header">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight" data-testid="page-title">
                Le mie Valutazioni
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">Gestisci il tuo carico di lavoro e le visite programmate</p>
            </div>

            {/* View Switcher (Touch-sized) */}
            <div className="flex items-center bg-muted rounded-xl p-1 shrink-0" data-testid="view-switcher">
              <button
                onClick={() => setViewMode('lista')}
                data-testid="view-lista-btn"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'lista' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <List className="w-4 h-4" />
                Lista
              </button>
              <button
                onClick={() => setViewMode('calendario')}
                data-testid="view-calendario-btn"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'calendario' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                Calendario
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-lg">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca paziente o codice fiscale..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 h-11 text-sm rounded-xl"
              data-testid="search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Tab Filters */}
          <div className="flex gap-3" data-testid="tab-filters">
            <button
              onClick={() => setTab('da_iniziare')}
              data-testid="tab-da-iniziare"
              className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${
                tab === 'da_iniziare'
                  ? 'border-warning bg-warning/10 text-foreground shadow-sm'
                  : 'border-border bg-card text-muted-foreground hover:border-warning/40'
              }`}
            >
              <Clock className="w-4.5 h-4.5" />
              Da Iniziare
              <Badge variant="warning" className="text-xs font-bold min-w-[26px] justify-center" data-testid="badge-da-iniziare">
                {daIniziare.length}
              </Badge>
            </button>
            <button
              onClick={() => setTab('completate')}
              data-testid="tab-completate"
              className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${
                tab === 'completate'
                  ? 'border-success bg-success/10 text-foreground shadow-sm'
                  : 'border-border bg-card text-muted-foreground hover:border-success/40'
              }`}
            >
              <CheckCircle2 className="w-4.5 h-4.5" />
              Completate
              <Badge variant="success" className="text-xs font-bold min-w-[26px] justify-center" data-testid="badge-completate">
                {completate.length}
              </Badge>
            </button>

            <span className="text-xs text-muted-foreground ml-auto self-center hidden sm:block" data-testid="results-count">
              {filtered.length} risultati
            </span>
          </div>
        </div>

        {/* ── Content ── */}
        <AnimatePresence mode="wait">
          {viewMode === 'lista' ? (
            <motion.div
              key="lista"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              data-testid="list-view"
            >
              {filtered.length === 0 ? (
                <Card className="shadow-card">
                  <CardContent className="p-12 text-center">
                    <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-foreground font-semibold">Nessuna valutazione trovata</p>
                    <p className="text-sm text-muted-foreground mt-1">Prova a modificare la ricerca o i filtri</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {filtered.map((patient, idx) => {
                    const pConf = priorityConfig[patient.priorita];
                    const isCompleted = patient.stato === 'pai_completato';
                    const visitDateObj = patient.visitDate;
                    const isTodayVisit = isToday(visitDateObj);

                    return (
                      <motion.div
                        key={patient.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                      >
                        <Card
                          data-testid={`patient-card-${patient.id}`}
                          className={`shadow-card hover:shadow-elevated transition-all duration-200 cursor-pointer active:scale-[0.98] ${
                            isTodayVisit && !isCompleted ? 'ring-2 ring-primary/30' : ''
                          }`}
                          onClick={() => navigate(`/medico/valutazione/${patient.id}`)}
                        >
                          <CardContent className="p-5">
                            {/* Top: Name + Priority */}
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  <User className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                  <p className="text-base font-bold text-foreground leading-tight">
                                    {patient.cognome} {patient.nome}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{patient.eta} anni</p>
                                </div>
                              </div>
                              <Badge variant={pConf.badge} className="shrink-0">
                                <span className={`w-1.5 h-1.5 rounded-full ${pConf.dot} mr-1.5`} />
                                {pConf.label}
                              </Badge>
                            </div>

                            {/* Diagnosis */}
                            <p className="text-sm text-foreground/80 mb-3 line-clamp-1">
                              {patient.diagnosiPrincipale?.split('(')[0].trim()}
                            </p>

                            {/* Meta row */}
                            <div className="flex flex-col gap-1.5 mb-4">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <MapPin className="w-3.5 h-3.5 shrink-0" />
                                <span className="truncate">{patient.indirizzo}</span>
                              </div>
                              {patient.dataVisitaUVMD && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <CalendarDays className="w-3.5 h-3.5 shrink-0" />
                                  <span>{patient.dataVisitaUVMD}</span>
                                  <span className="text-foreground/60">&middot;</span>
                                  <Clock className="w-3 h-3 shrink-0" />
                                  <span>{patient.oraVisita}</span>
                                </div>
                              )}
                            </div>

                            {/* Status + Action */}
                            <div className="flex items-center justify-between pt-3 border-t border-border">
                              {isTodayVisit && !isCompleted ? (
                                <Badge variant="chip" className="bg-primary/10 text-primary border border-primary/20 text-xs font-semibold">
                                  Programmata per oggi {patient.oraVisita}
                                </Badge>
                              ) : isCompleted ? (
                                <Badge variant="success" className="text-xs">Completata</Badge>
                              ) : (
                                <Badge className="bg-muted text-muted-foreground text-xs">
                                  {formatRelativeDate(visitDateObj)}
                                </Badge>
                              )}

                              <div className="flex items-center gap-1 text-primary text-sm font-semibold">
                                {isCompleted ? (
                                  <>
                                    <Eye className="w-4 h-4" />
                                    <span>Dettaglio</span>
                                  </>
                                ) : (
                                  <>
                                    <Stethoscope className="w-4 h-4" />
                                    <span>Valuta</span>
                                    <ArrowRight className="w-3.5 h-3.5" />
                                  </>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="calendario"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="shadow-card">
                <CardContent className="p-5">
                  <MonthCalendar
                    events={calEvents}
                    month={calMonth}
                    year={calYear}
                    onNav={handleCalNav}
                    onEventClick={(id) => navigate(`/medico/valutazione/${id}`)}
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
